import { Inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { NavigationEnd, Router } from '@angular/router';
import { TRANSLOCO_LOADER, TranslocoService } from '@ngneat/transloco';
import { AppEnvironmentConfig } from 'projects/config/model/environment.config.model';
import { generateModuleMenuURL, getModuleMenuType } from 'projects/lib-shared-common/src/public-api';
import {
  EMPTY,
  Observable,
  ReplaySubject,
  Subject,
  catchError,
  debounceTime,
  delay,
  filter,
  finalize,
  first,
  map,
  merge,
  mergeWith,
  of,
  shareReplay,
  switchMap,
  takeUntil,
  tap,
  throwError,
} from 'rxjs';
import { StaticApplicationId } from '../../../Constants/static-application-id.enum';
import { ParameterService } from '../../../services/parameter/parameter.service';
import { CMMS_CHECKLIST_MENU_APPLICATIONS, CMMS_FLAG_PARAMETERS, isCmmsChecklistMenuUrl } from '../../../services/parameter/cmms-flag.constants';
import { NgxPermissionsService } from 'ngx-permissions';
import { ICONMENU } from './icon-menu-map';
import { Navigation, SvcDefaultNavigationItem, SvcMenuNavigation, SvcMenuNavigationItemType, SvcWorkspaceNavigationItem } from './navigation.types';
import { ADMIN_PERMISSION } from '../../../auth/auth-permissions.constants';
import { AuthService } from '../../../auth/auth.service';
import { AuthUtils } from '../../../auth/auth.utils';
import { SvcDataCacheService } from '../../../services/cache/svc-data-cache.service';
import { SvcCacheName } from '../../../services/cache/svc-cache-name.enum';
import { TranslocoApiLoader, TranslocoTermsStatus } from '../../transloco/transloco.api-loader';

@Injectable({
  providedIn: 'root',
})
export class NavigationService {
  private _pendingNavigationRequest: Subject<SvcMenuNavigation[]>;
  private _navigation: ReplaySubject<Navigation> = new ReplaySubject<Navigation>(1);
  private navigation: Navigation;
  public navigation$ = this._navigation.asObservable();
  private _aplicationId: StaticApplicationId;
  public error: unknown;
  public isLoading: boolean = true;

  private set _navigationCached(value: SvcMenuNavigation[]) {
    this._dataCacheService.set(`MENU_GROUP_${this._aplicationId}` as SvcCacheName, value, { hours: 2 });
  }
  private get _navigationCached(): SvcMenuNavigation[] {
    return this._dataCacheService.get(`MENU_GROUP_${this._aplicationId}` as SvcCacheName);
  }

  private _setRoleSignature(applicationId: string, value: string): void {
    this._dataCacheService.set(`ROLE_SIG_${applicationId}` as SvcCacheName, value, { hours: 168 });
  }
  private _getRoleSignature(applicationId: string): string | null {
    return this._dataCacheService.get(`ROLE_SIG_${applicationId}` as SvcCacheName);
  }

  constructor(
    private _httpClient: HttpClient,
    private _translocoService: TranslocoService,
    private _appConfig: AppEnvironmentConfig,
    private _authService: AuthService,
    private _dataCacheService: SvcDataCacheService,
    private _router: Router,
    private _permissionsService: NgxPermissionsService,
    @Inject(TRANSLOCO_LOADER) private _translocoTermsService: TranslocoApiLoader,
    private _parameterService: ParameterService
  ) {
    merge(this._authService.signIn$, this._authService.signOut$).subscribe((logged) => {
      if (logged?.success) {
        this.reset();
      }
    });

    this._router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        debounceTime(500),
        filter(() => !!this._aplicationId)
      )
      .subscribe(() => this._checkRolesFreshness());
  }

  private _checkRolesFreshness(): void {
    const applicationId = this._aplicationId;
    if (!applicationId) return;
    const token = this._authService.accessToken;
    if (!token || AuthUtils.isTokenExpired(token)) return;
    this._httpClient
      .get<{ roleId: string; roleName: string }[]>(`${this._appConfig.APIs.apiUrlAuth}/roles/user/${applicationId}`)
      .pipe(catchError(() => of(null)))
      .subscribe((roles) => {
        if (!Array.isArray(roles)) return;
        const signature = roles
          .map((r) => r?.roleId ?? '')
          .filter(Boolean)
          .sort()
          .join('|');
        const previous = this._getRoleSignature(applicationId);
        const hadPreExistingMenuCache = !!this._dataCacheService.get(`MENU_GROUP_${applicationId}` as SvcCacheName);
        this._setRoleSignature(applicationId, signature);

        if (previous === null) {
          if (hadPreExistingMenuCache) {
            this._navigationCached = null;
            globalThis.location.reload();
          }
          return;
        }

        if (previous !== signature) {
          this._navigationCached = null;
          globalThis.location.reload();
        }
      });
  }
  public reset(): void {
    this._navigationCached = null;
    this.navigation = null;
  }

  get(aplicationId?: StaticApplicationId): Observable<Navigation> {
    const defaultSvc: SvcDefaultNavigationItem[] = [];
    const defaultSvcWorkspace: SvcWorkspaceNavigationItem[] = [];

    const defaultNavigation = {
      default: defaultSvc,
      workspace: defaultSvcWorkspace,
    };

    if (!aplicationId) {
      this._navigation.next(defaultNavigation);
      return of(defaultNavigation);
    }

    this._aplicationId = aplicationId;
    setTimeout(() => this._checkRolesFreshness(), 0);
    if (this.navigation) {
      this.navigation = this.sanitizeActionPlanNavigation(this.navigation);
      return of(this.navigation);
    }

    this.error = null;
    this.isLoading = true;
    let fromCached = false;

    const responseSubject = new Subject<void>();
    this._translocoTermsService.termsStatus$
      .pipe(
        takeUntil(responseSubject.pipe(mergeWith(this._authService.signOut$))),
        filter((status) => status === TranslocoTermsStatus.LOADED),
        delay(0),
        tap(() => {
          responseSubject.next();
          responseSubject.complete();
        })
      )
      .subscribe();
    return responseSubject.pipe(
      switchMap(() => {
        if (this._aplicationId === StaticApplicationId.digitalObeya) {
          return of([] as SvcMenuNavigation[]);
        }
        const cache = this._navigationCached;
        fromCached = Array.isArray(cache) && cache.length > 0;
        if (fromCached) return of(cache);
        if (!this._pendingNavigationRequest) {
          this._pendingNavigationRequest = new Subject<SvcMenuNavigation[]>();
          const subject = this._pendingNavigationRequest;
          const clear = (): void => (this._pendingNavigationRequest = null);
          this._httpClient
            .get<SvcMenuNavigation[]>(`${this._appConfig.APIs.apiUrlAdministration}/MenuGroup/module/${this._aplicationId}`)
            .pipe(
              tap((response) => {
                clear();
                subject.next(response);
              }),
              catchError((error) => {
                clear();
                subject.error(error);
                return EMPTY;
              }),
              finalize(() => {
                clear();
                subject.complete();
              })
            )
            .subscribe();
        }
        return this._pendingNavigationRequest;
      }),
      switchMap((items) => this.resolveCmmsChecklistVisibility(items)),
      map(({ items, hideChecklist }) => {
        if (!fromCached) this._navigationCached = items;

        const menuItems: SvcDefaultNavigationItem[] = this.removeCmmsChecklistItems(this.mapMenuNavigationItem(items), hideChecklist);
        const workspaceItems: SvcWorkspaceNavigationItem[] = this.removeCmmsChecklistItems(this.mapMenuToSvcWorkspaceNavigationItem(items), hideChecklist);

        if (this._aplicationId === StaticApplicationId.actionPlanRevamp) {
          const settingsGroup = menuItems.find((m) => m.title === 'Configurações' || m.title === 'Settings');
          if (settingsGroup) {
            const exists = settingsGroup.children.some((c) => (c.url as string)?.includes('priorities-registration'));
            if (!exists) {
              const translatedTitle = this._translocoService.translate('Cadastro de prioridades');
              settingsGroup.children.push({
                title: translatedTitle === 'Priority registration' ? 'Priorities Registration' : translatedTitle,
                url: '/settings/priorities-registration',
                children: [],
              });
            }
          }
        }

        if (this._aplicationId === StaticApplicationId.digitalObeya) {
          // AB#68772 — "Cadastro de Tipos" is admin-only: same permission key as the route guard.
          const isObeyaAdmin = !!this._permissionsService.getPermission(ADMIN_PERMISSION);
          const MOCK_DIGITAL_OBEYA_NAV: SvcDefaultNavigationItem[] = [
            {
              title: this._translocoService.translate('Meu Perfil'),
              icon: 'mat_solid:person',
              children: [
                {
                  title: this._translocoService.translate('Minhas Salas (Proprietário)'),
                  url: '/profile/my-rooms/owner',
                  children: [],
                },
                {
                  title: this._translocoService.translate('Minhas Salas (Membro)'),
                  url: '/profile/my-rooms/member',
                  children: [],
                },
              ],
            },
            {
              title: this._translocoService.translate('Busca'),
              icon: 'mat_solid:search',
              children: [
                { title: this._translocoService.translate('Busca de Salas'), url: '/search/obeya-board/all', children: [] },
                { title: this._translocoService.translate('Busca de Planos de Ação'), url: '/search/action-plans', children: [] },
              ],
            },
            {
              title: this._translocoService.translate('Configurações'),
              icon: 'mat_solid:settings',
              children: [
                ...(isObeyaAdmin
                  ? [{ title: this._translocoService.translate('Cadastro de Tipos'), url: '/settings/types', children: [] }]
                  : []),
                { title: this._translocoService.translate('Gestão de Notificações'), url: '/settings/notifications', children: [] },
                { title: this._translocoService.translate('Cadastro de Tópicos'), url: '/settings/topics', children: [] },
                { title: this._translocoService.translate('Permissões de Acesso'), url: '/settings/permissions', children: [] },
              ],
            },
            {
              title: this._translocoService.translate('Suporte'),
              icon: 'mat_solid:menu_book',
              children: [
                {
                  title: this._translocoService.translate('Perguntas frequentes'),
                  url: '/help/faq',
                  queryParams: { title: this._translocoService.translate('Perguntas frequentes') },
                  children: [],
                },
                {
                  title: this._translocoService.translate('Processo'),
                  url: '/help/process',
                  queryParams: { title: this._translocoService.translate('Processo') },
                  children: [],
                },
              ],
            },
          ];

          for (const mockGroup of MOCK_DIGITAL_OBEYA_NAV) {
            const existingGroup = menuItems.find((group) => group.title === mockGroup.title);
            if (!existingGroup) {
              menuItems.push(mockGroup);
              continue;
            }

            for (const child of mockGroup.children) {
              const alreadyPresent = existingGroup.children.some((item) => (item.url as string) === child.url);
              if (!alreadyPresent) {
                existingGroup.children.push(child);
              }
            }
          }
        }

        if (this._aplicationId === StaticApplicationId.administration) {
          const MOCK_ADM_NEW_FEATURES_ITEMS: SvcDefaultNavigationItem[] = [
            { title: this._translocoService.translate('Material treinamento'), url: '/solvace-customer/training-material', children: [] },
            { title: this._translocoService.translate('Parâmetros do cliente'), url: '/solvace-customer/client-params', children: [] },
          ];

          const MOCK_ADM_NEW_FEATURES_MISSING = MOCK_ADM_NEW_FEATURES_ITEMS.filter((item) => !menuItems.some((g) => g.children?.some((c) => (c.url as string) === item.url)));

          if (MOCK_ADM_NEW_FEATURES_MISSING.length > 0) {
            menuItems.push({
              title: this._translocoService.translate('Admin new features'),
              icon: 'mat_solid:settings',
              children: MOCK_ADM_NEW_FEATURES_MISSING,
            });
          }

          const MOCK_ADM_GROUP_TITLE = this._translocoService.translate('Admin new features');

          const MOCK_ADMIN_ITEMS_TO_INJECT: SvcDefaultNavigationItem[] = [
            (() => {
              const t = this._translocoService.translate('Perguntas frequentes');
              return { title: t, url: '/help/faq', queryParams: { title: t }, children: [] };
            })(),
            { title: this._translocoService.translate('Funcionalidades'), url: '/solvace-site/functionalities-new', children: [] },
            { title: this._translocoService.translate('Pesquisa de satisfação'), url: '/solvace-site/satisfaction-survey', children: [] },
            { title: this._translocoService.translate('Menu de módulos - grupos'), url: '/solvace-customer/menu-modules-groups', children: [] },
            { title: this._translocoService.translate('Ordenação de grupos'), url: '/solvace-customer/groups-order', children: [] },
          ];

          for (const item of MOCK_ADMIN_ITEMS_TO_INJECT) {
            const adminGroup = menuItems.find((m) => m.title === MOCK_ADM_GROUP_TITLE);
            const alreadyAdded = adminGroup?.children?.some((c) => (c.url as string) === item.url);
            if (adminGroup && !alreadyAdded) {
              adminGroup.children.push(item);
            } else if (!adminGroup) {
              menuItems.push({ title: MOCK_ADM_GROUP_TITLE, icon: 'mat_solid:settings', children: [item] });
            }
          }

          const MOCK_ADM_CLIENT_PARAMS_PRESENT = menuItems.some((g) => g.children?.some((c) => (c.url as string) === '/solvace-customer/client-params'));
          if (!MOCK_ADM_CLIENT_PARAMS_PRESENT) {
            const solvaceCustomerGroup = menuItems.find((m) => m.title === 'Solvace - Customer' || m.title === 'Solvace - Cliente');
            if (solvaceCustomerGroup) {
              solvaceCustomerGroup.children.push({ title: this._translocoService.translate('Parâmetros do cliente'), url: '/solvace-customer/client-params', children: [] });
            } else {
              menuItems.push({
                title: this._translocoService.translate('Solvace - Customer'),
                icon: 'mat_solid:business',
                children: [{ title: this._translocoService.translate('Parâmetros do cliente'), url: '/solvace-customer/client-params', children: [] }],
              });
            }
          }
        }

        this.navigation = this.sanitizeActionPlanNavigation({
          default: menuItems,
          workspace: workspaceItems,
        });
        this._navigation.next(this.navigation);
        this.isLoading = false;

        return this.navigation;
      }),
      catchError((error) => {
        this.error = error;
        this.isLoading = false;

        return throwError(() => error);
      })
    );
  }

  /**
   * Get all navigation data
   */
  isAvailable(aplicationId: string): Observable<boolean> {
    return this._httpClient.get<boolean>(`${this._appConfig.APIs.apiUrlAdministration}/MenuGroup/module/${aplicationId}/accessible`);
  }

  private resolveCmmsChecklistVisibility(items: SvcMenuNavigation[]): Observable<{ items: SvcMenuNavigation[]; hideChecklist: boolean }> {
    const module = CMMS_CHECKLIST_MENU_APPLICATIONS[this._aplicationId];
    if (!module) {
      return of({ items, hideChecklist: false });
    }

    return this._parameterService.getSiteParam(CMMS_FLAG_PARAMETERS[module]).pipe(
      map((value) => ({ items, hideChecklist: value?.toUpperCase() !== 'TRUE' })),
      catchError(() => of({ items, hideChecklist: false }))
    );
  }

  private removeCmmsChecklistItems<T extends { url?: SvcDefaultNavigationItem['url']; children?: T[] }>(groups: T[], hideChecklist: boolean): T[] {
    if (!hideChecklist) {
      return groups;
    }

    return groups.map((group) => ({
      ...group,
      children: (group.children ?? []).filter((item) => !isCmmsChecklistMenuUrl(item.url)),
    }));
  }

  private mapMenuNavigationItem(menuItems: SvcMenuNavigation[]): SvcDefaultNavigationItem[] {
    const navigationItem: SvcDefaultNavigationItem[] = [];

    menuItems.forEach((item) => {
      const children = this.hasChildren(item)
        ? item.menus.map((c) => {
            const itemType = c.menuCategoryProvider ? getModuleMenuType(SvcMenuNavigationItemType[c.menuCategoryProvider.applicationType]) : null;
            const rawUrl =
              itemType === 'link'
                ? generateModuleMenuURL(
                    SvcMenuNavigationItemType[c.menuCategoryProvider.applicationType],
                    c.menuCategoryProvider.url,
                    c.menuCategoryProvider.route,
                    c.menuCategoryProvider.params
                  )
                : null;
            const split = this.splitNormalizedNavigationUrl(this.normalizeActionPlanNavigationUrl(rawUrl));
            return {
              title: c.menuDesc,
              url: split.url,
              queryParams: split.queryParams ?? (c.menuCategoryProvider.route === '/help/faq' ? { title: c.menuDesc } : null),
              children: [],
            } as SvcDefaultNavigationItem;
          })
        : [];

      const normalizedChildren = this.removeDuplicatedActionPlanChildren(children);
      const orderedChildren = this.orderAlertSettingsChildren(this.orderActionPlanSettingsChildren(normalizedChildren));

      navigationItem.push(<SvcDefaultNavigationItem>{
        title: this._translocoService.translate(item.menuGroupName),
        icon: ICONMENU.getValue(item.menuGroupId) || '',
        children: orderedChildren,
      });
    });

    return navigationItem;
  }

  private mapMenuToSvcWorkspaceNavigationItem(menuItems: SvcMenuNavigation[]): SvcWorkspaceNavigationItem[] {
    const navigationItem: SvcWorkspaceNavigationItem[] = [];

    menuItems.forEach((item) => {
      if (this.hasChildren(item)) {
        item.menus.map((c) => {
          const rawUrl = c.menuCategoryProvider
            ? generateModuleMenuURL(
                SvcMenuNavigationItemType[c.menuCategoryProvider.applicationType],
                c.menuCategoryProvider.url,
                c.menuCategoryProvider.route,
                c.menuCategoryProvider.params
              )
            : null;
          const split = this.splitNormalizedNavigationUrl(this.normalizeActionPlanNavigationUrl(rawUrl));

          navigationItem.push({
            icon: ICONMENU.getValue(item.menuGroupId) || '',
            url: split.url,
            queryParams: split.queryParams,
            title: c.menuDesc,
            params: c.menuCategoryProvider.params,
          });
        });
      }
    });
    return this.removeDuplicatedActionPlanWorkspaceItems(navigationItem);
  }

  /**
   * Splits a normalized URL (which may carry `?queryString`) into a clean
   * `url` (path only) and `queryParams` map suitable for Angular's router.
   * Non-string inputs pass through unchanged.
   */
  private splitNormalizedNavigationUrl(normalized: SvcDefaultNavigationItem['url'] | null): { url: SvcDefaultNavigationItem['url'] | null; queryParams: Record<string, string> | null } {
    if (typeof normalized !== 'string') return { url: normalized, queryParams: null };
    const [path, query] = normalized.split('?');
    if (!query) return { url: normalized, queryParams: null };
    const queryParams: Record<string, string> = {};
    new URLSearchParams(query).forEach((value, key) => {
      queryParams[key] = value;
    });
    return { url: path, queryParams };
  }

  private hasChildren(menuItem: SvcMenuNavigation): boolean {
    return !!menuItem.menus;
  }

  private isActionPlanModuleNavigation(): boolean {
    return [StaticApplicationId.actionPlan, StaticApplicationId.actionPlanCore, StaticApplicationId.actionPlanRevamp].includes(this._aplicationId);
  }

  private normalizeActionPlanNavigationUrl(url: SvcDefaultNavigationItem['url'] | null): SvcDefaultNavigationItem['url'] | null {
    if (!this.isActionPlanModuleNavigation()) return url;
    if (typeof url !== 'string' || !url.startsWith('/')) return url;

    const [pathPart, queryPart] = url.split('?');
    const normalizedPath = pathPart.trim().toLowerCase().replace(/\/+$/, '');

    const aliases: Record<string, string> = {
      '/my-action-plans/responsible': '/search/action-plan-search?tab=responsible',
      '/my-action-plans/originator': '/search/action-plan-search?tab=originator',
      '/my-action-plans/approvals': '/search/action-plan-search?tab=approver',
      '/search/action-plan': '/search/action-plan-search',
      '/search/actionplan-search': '/search/action-plan-search',
      '/analytics/byperiod': '/analytics/by-period',
      '/analytics/byoriginator': '/analytics/by-responsible',
      '/analytics/byresponsible': '/analytics/by-responsible',
      '/analytics/by-originator': '/analytics/by-responsible',
      '/analytics/action-plan-analytic/dashboard': '/analytics/dashboard',
      '/analytics/action-plan-analytic/byperiod': '/analytics/by-period',
      '/analytics/action-plan-analytic/byoriginator': '/analytics/by-responsible',
      '/analytics/action-plan-analytic/byresponsible': '/analytics/by-responsible',
      '/analytics/action-plan-analytic/bysystem': '/analytics/by-system',
      '/analytics/action-plan-analytic/byage': '/analytics/by-age',
      '/analytics/action-plan-analytic/bystatus': '/analytics/by-status',
      '/analytics/bysystem': '/analytics/by-system',
      '/analytics/byage': '/analytics/by-age',
      '/analytics/bystatus': '/analytics/by-status',
      '/export/acpdatabase': '/analytics/database',
      '/settings/change-responsible': '/settings/responsible-change',
      '/settings/access-permission': '/settings/access-permissions',
      '/settings/system-registration': '/settings/system-management',
      '/settings/systems-registration': '/settings/system-management',
      '/settings/group-registration': '/settings/group-management',
      '/settings/groups-registration': '/settings/group-management',
    };

    const route = aliases[normalizedPath] ?? normalizedPath;
    return queryPart ? `${route}?${queryPart}` : route;
  }

  private removeDuplicatedActionPlanChildren(children: SvcDefaultNavigationItem[]): SvcDefaultNavigationItem[] {
    if (!this.isActionPlanModuleNavigation()) return children;

    const seen = new Set<string>();
    return children.filter((child) => {
      if (typeof child.url === 'string' && child.url.startsWith('/') && !this.isAllowedActionPlanRoute(child.url)) {
        return false;
      }

      const key = this.getActionPlanNavigationDedupKey(child.url, child.title, child.queryParams);

      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  private removeDuplicatedActionPlanWorkspaceItems(items: SvcWorkspaceNavigationItem[]): SvcWorkspaceNavigationItem[] {
    if (!this.isActionPlanModuleNavigation()) return items;

    const seen = new Set<string>();
    return items.filter((item) => {
      if (typeof item.url === 'string' && item.url.startsWith('/') && !this.isAllowedActionPlanRoute(item.url)) {
        return false;
      }

      const key = this.getActionPlanNavigationDedupKey(item.url, item.title, item.queryParams);

      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  private getActionPlanNavigationDedupKey(url: SvcDefaultNavigationItem['url'] | null, title?: string, queryParams?: Record<string, unknown> | null): string {
    if (typeof url === 'string' && url.startsWith('/')) {
      const normalizedUrl = this.normalizeActionPlanNavigationUrl(url);
      if (typeof normalizedUrl === 'string') {
        const [pathPart, queryPart] = normalizedUrl.split('?');
        const normalizedPath = pathPart.trim().toLowerCase().replace(/\/+$/, '');
        const querySignature = this.stringifyQueryForDedup(queryParams) || (queryPart ? queryPart.trim().toLowerCase() : '');
        return querySignature ? `url:${normalizedPath}?${querySignature}` : `url:${normalizedPath}`;
      }
    }

    return `title:${(title ?? '').trim().toLowerCase()}`;
  }

  private stringifyQueryForDedup(queryParams: Record<string, unknown> | null | undefined): string {
    if (!queryParams) return '';
    const entries = Object.entries(queryParams)
      .filter(([, v]) => v !== undefined && v !== null)
      .map(([k, v]) => [k.toLowerCase(), String(v).toLowerCase()] as const)
      .sort(([a], [b]) => a.localeCompare(b));
    return entries.map(([k, v]) => `${k}=${v}`).join('&');
  }

  private isAllowedActionPlanRoute(url: string): boolean {
    const path = url.split('?')[0].toLowerCase().replace(/\/+$/, '');
    const allowedRoutes = new Set([
      '/search/action-plan-search',
      '/analytics/dashboard',
      '/analytics/by-period',
      '/analytics/by-responsible',
      '/analytics/by-system',
      '/analytics/by-age',
      '/analytics/by-status',
      '/analytics/database',
      '/settings/responsible-change',
      '/settings/fields-management',
      '/settings/system-management',
      '/settings/system-registration',
      '/settings/systems-registration',
      '/settings/group-management',
      '/settings/priorities-registration',
      '/settings/notification-management',
      '/settings/access-permissions',
      '/help/faq',
      '/help/process',
    ]);

    return allowedRoutes.has(path);
  }

  private sanitizeActionPlanNavigation(navigation: Navigation): Navigation {
    if (!this.isActionPlanModuleNavigation()) return navigation;

    return {
      default: (navigation.default ?? []).map((item) => ({
        ...item,
        children: this.orderActionPlanSettingsChildren(this.removeDuplicatedActionPlanChildren(item.children ?? [])),
      })),
      workspace: this.removeDuplicatedActionPlanWorkspaceItems(navigation.workspace ?? []),
    };
  }

  private orderActionPlanSettingsChildren(children: SvcDefaultNavigationItem[]): SvcDefaultNavigationItem[] {
    if (!this.isActionPlanModuleNavigation() || !children?.length) return children;

    const isSettingsGroup = children.some((child) => typeof child.url === 'string' && child.url.startsWith('/settings/'));
    if (!isSettingsGroup) return children;

    const order: Record<string, number> = {
      '/settings/responsible-change': 1,
      '/settings/fields-management': 2,
      '/settings/system-management': 3,
      '/settings/group-management': 4,
      '/settings/priorities-registration': 5,
      '/settings/notification-management': 6,
      '/settings/access-permissions': 7,
    };

    return children
      .map((item, index) => ({ item, index }))
      .sort((a, b) => {
        const aPath = typeof a.item.url === 'string' ? (this.normalizeActionPlanNavigationUrl(a.item.url) as string)?.split('?')[0] : '';
        const bPath = typeof b.item.url === 'string' ? (this.normalizeActionPlanNavigationUrl(b.item.url) as string)?.split('?')[0] : '';
        const aOrder = order[aPath] ?? Number.MAX_SAFE_INTEGER;
        const bOrder = order[bPath] ?? Number.MAX_SAFE_INTEGER;

        if (aOrder !== bOrder) return aOrder - bOrder;
        return a.index - b.index;
      })
      .map((entry) => entry.item);
  }

  private orderAlertSettingsChildren(children: SvcDefaultNavigationItem[]): SvcDefaultNavigationItem[] {
    if (this._aplicationId !== StaticApplicationId.alert || !children?.length) return children;

    const order: Record<string, number> = {
      '/settings/distribution-management': 1,
      '/settings/field-registration': 2,
      '/settings/notification-management': 3,
      '/settings/access-permission': 4,
    };

    const pathOf = (item: SvcDefaultNavigationItem): string => (typeof item.url === 'string' ? item.url.split('?')[0] : '');
    if (!children.some((child) => pathOf(child) in order)) return children;

    return children
      .map((item, index) => ({ item, index }))
      .sort((a, b) => {
        const aOrder = order[pathOf(a.item)] ?? Number.MAX_SAFE_INTEGER;
        const bOrder = order[pathOf(b.item)] ?? Number.MAX_SAFE_INTEGER;

        if (aOrder !== bOrder) return aOrder - bOrder;
        return a.index - b.index;
      })
      .map((entry) => entry.item);
  }
}
